/**
 * 预览纵横比；尺寸枚举以服务端配置为准，
 * 预览宽高见 `lib/clientUiImageModel.previewDimensionsFromClientUiModel`。
 */

import type { CSSProperties } from 'react'

import type { ImageGenParams } from '../types/timeline'

export type GptImageQuality = 'low' | 'medium' | 'high' | 'auto'

/**
 * 时间线图片区域：用快照 width/height 设置真实 CSS aspect-ratio（避免把 2:3 误套成 9:16）。
 * 智能体写入的轮次没有宽高快照，回退用 params.aspect_ratio（"1:1"）或 params.size（"1024x1024"）换算，
 * 保证生成中骨架屏与成图区域比例一致，不再退化为默认宽框。
 */
export function timelineImageAspectStyle(params: ImageGenParams | undefined): CSSProperties | undefined {
  if (
    params != null &&
    typeof params.width === 'number' &&
    typeof params.height === 'number' &&
    params.width > 0 &&
    params.height > 0
  ) {
    return { aspectRatio: `${params.width} / ${params.height}` }
  }
  if (params == null) return undefined
  const aspectRatio = typeof params.aspect_ratio === 'string' ? params.aspect_ratio.trim() : ''
  const ratioMatch = /^(\d+(?:\.\d+)?)\s*[:：]\s*(\d+(?:\.\d+)?)$/.exec(aspectRatio)
  if (ratioMatch) {
    return { aspectRatio: `${ratioMatch[1]} / ${ratioMatch[2]}` }
  }
  const size = typeof params.size === 'string' ? params.size.trim().toLowerCase() : ''
  const sizeMatch = /^(\d{2,5})\s*[x×]\s*(\d{2,5})$/.exec(size)
  if (sizeMatch) {
    return { aspectRatio: `${sizeMatch[1]} / ${sizeMatch[2]}` }
  }
  return undefined
}

/** 无有效宽高快照时的最小高度（与时间线卡片约 0.75 宽度配套，较原 240px 缩小 25%） */
export function timelineImageMinHeightClass(params: ImageGenParams | undefined): string {
  const style = timelineImageAspectStyle(params)
  return style ? '' : 'min-h-[180px]'
}

/**
 * 时间线图框等比限高：竖图以 360px 为高度上限、等比缩窄宽度后水平居中；
 * 横图/方图保持整宽（受外层卡片宽度约束）。与智能体生图卡片骨架同一规范（9:16 不再撑满整屏）。
 *
 * 入参为 {@link timelineImageAspectStyle} 返回的 `{ aspectRatio: "W / H" }`，缺省比例时退化为仅限高 + 居中。
 */
export function timelineFrameClampStyle(style: CSSProperties | undefined): CSSProperties {
  const aspectRaw = style?.aspectRatio
  const ratioMatch = /^(\d+(?:\.\d+)?)\s*[/]\s*(\d+(?:\.\d+)?)$/.exec(String(aspectRaw ?? ''))
  if (!ratioMatch) {
    // 无明确比例：仅限制最大高度并水平居中，宽度交给外层卡片
    return { maxHeight: '360px', marginInline: 'auto' }
  }
  const ratioW = Number.parseFloat(ratioMatch[1])
  const ratioH = Number.parseFloat(ratioMatch[2])
  if (!Number.isFinite(ratioW) || !Number.isFinite(ratioH) || ratioW <= 0 || ratioH <= 0) {
    return { maxHeight: '360px', marginInline: 'auto' }
  }
  return {
    aspectRatio: `${ratioW} / ${ratioH}`,
    maxHeight: '360px',
    width: `min(100%, calc(360px * ${ratioW} / ${ratioH}))`,
    marginInline: 'auto',
  }
}
